import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { fetchBlogPost } from "@/lib/api";
import { waLink, WA_MESSAGES } from "@/lib/whatsapp";
import { SITE_URL, absUrl } from "@/lib/site";
import SEO from "@/components/SEO";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);

  useEffect(() => {
    fetchBlogPost(slug)
      .then((p) => setPost(p))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!post) return <div className="section-pad container-x">Loading…</div>;

  const absImage = absUrl(post.cover);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: absImage,
    datePublished: post.date,
    author: { "@type": "Person", name: post.author },
    publisher: {
      "@type": "Organization",
      name: "Weight Loss Gurukul",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/logo192.png` },
    },
    keywords: (post.tags || []).join(", "),
    mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
  };

  return (
    <article data-testid="blog-post" className="section-pad">
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.cover}
        type="article"
        keywords={(post.tags || []).join(", ")}
        jsonLd={articleJsonLd}
      />
      <div className="container-x max-w-3xl">
        <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-forest">
          <ArrowLeft className="h-4 w-4" /> All articles
        </Link>

        <div className="mt-6 flex flex-wrap gap-2">
          {post.tags.map((t) => <span key={t} className="text-xs px-2 py-1 rounded-full bg-sand-200 text-forest">{t}</span>)}
        </div>
        <h1 className="mt-4 font-serif text-4xl md:text-5xl text-forest leading-tight">{post.title}</h1>
        <div className="mt-3 text-sm text-ink-muted">
          {post.author} · {new Date(post.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })} · {post.read_time}
        </div>

        <div className="mt-8 rounded-3xl overflow-hidden border border-sand-300">
          <img src={post.cover} alt={post.title} className="w-full aspect-[16/9] object-cover" />
        </div>

        <p className="mt-8 font-serif text-2xl text-forest italic leading-snug">{post.excerpt}</p>

        <div className="mt-8 prose-content text-ink-soft leading-loose text-lg space-y-5">
          {renderContent(post.content, post.slug)}
        </div>

        <div className="mt-16 rounded-3xl bg-forest text-white p-8 md:p-10 text-center">
          <p className="eyebrow text-clay-light">Ready to practice?</p>
          <h3 className="mt-3 font-serif text-2xl md:text-3xl">Start your transformation this week.</h3>
          <a
            href={waLink(WA_MESSAGES.trial)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="blogpost-whatsapp-cta"
            className="btn-accent mt-6 inline-flex"
          >
            Book Your Free Consultation
          </a>
        </div>
      </div>
    </article>
  );
}

/**
 * Render blog body with light markdown-style support:
 *   "## Heading"   → <h2>
 *   "- item"       → <li> within a <ul>
 *   "**bold**"     → inline <strong>
 *   "*italic*"     → inline <em>
 */
function renderContent(content, slug) {
  const blocks = content.split("\n\n");
  const out = [];
  let listBuffer = [];

  const flushList = () => {
    if (!listBuffer.length) return;
    const ulIndex = out.length;
    out.push(
      <ul key={`${slug}-ul-${ulIndex}`} className="list-disc pl-6 space-y-2">
        {listBuffer.map((item, i) => (
          <li key={`${slug}-ul-${ulIndex}-li-${i}-${item.slice(0, 24)}`}>
            {renderInline(item)}
          </li>
        ))}
      </ul>
    );
    listBuffer = [];
  };

  blocks.forEach((block, i) => {
    const trimmed = block.trim();
    if (!trimmed) return;

    if (trimmed.startsWith("## ")) {
      flushList();
      out.push(
        <h2 key={`${slug}-h-${i}`} className="font-serif text-2xl md:text-3xl text-forest leading-tight pt-3">
          {trimmed.replace(/^##\s+/, "")}
        </h2>
      );
    } else if (trimmed.split("\n").every((l) => l.trim().startsWith("- "))) {
      // Whole block is a list
      const items = trimmed.split("\n").map((l) => l.replace(/^-\s+/, ""));
      listBuffer.push(...items);
      flushList();
    } else {
      flushList();
      out.push(
        <p key={`${slug}-p-${i}`}>{renderInline(trimmed)}</p>
      );
    }
  });
  flushList();
  return out;
}

function renderInline(text) {
  // Replace **bold** and *italic* (simple, safe pass)
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const m = match[0];
    if (m.startsWith("**")) {
      parts.push(<strong key={`b-${key++}`} className="text-forest font-semibold">{m.slice(2, -2)}</strong>);
    } else {
      parts.push(<em key={`i-${key++}`}>{m.slice(1, -1)}</em>);
    }
    lastIndex = match.index + m.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}
